// src/context/LPStakingContext.js

import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useContext,
} from 'react';
import { WalletContext } from './WalletContext';
import { toast } from 'react-toastify';
import { ethers } from 'ethers';
import LPStakingABI from '../abi/LPCashStake.json';
import {
  LPCASHSTAKE_ADDRESS,
  NETWORK_CONFIG,
  LPTOKEN_ADDRESS,
  CASHISDEAD_ADDRESS,
} from '../contracts/constants';

// Minimal Uniswap V3 Position Manager ABI for LP NFTs
const UniswapV3PositionManagerABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)",
  "function positions(uint256 tokenId) view returns (tuple(uint96 nonce,address operator,address token0,address token1,uint24 fee,int24 tickLower,int24 tickUpper,uint128 liquidity,uint256 feeGrowthInside0LastX128,uint256 feeGrowthInside1LastX128,uint128 tokensOwed0,uint128 tokensOwed1))",
  "function approve(address to, uint256 tokenId) external",
  "function getApproved(uint256 tokenId) view returns (address)"
];

export const LPStakingContext = createContext();

const LPStakingProvider = ({ children }) => {
  const { provider, account, chainId, isChangingWallet } = useContext(WalletContext);

  // Basic flags
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  // Data states
  const [lpStakingPositions, setLpStakingPositions] = useState([]);
  const [userLPNFTs, setUserLPNFTs] = useState([]);
  const [totalLPStaked, setTotalLPStaked] = useState('0');
  const [userLPBalance, setUserLPBalance] = useState('0');
  const [userLPAllowance, setUserLPAllowance] = useState('0');
  const [userBoostPercentage, setUserBoostPercentage] = useState(0);
  const [lpNFTsSupported, setLpNFTsSupported] = useState(false);

  // Action loading states
  const [loadingStake, setLoadingStake] = useState(false);
  const [loadingUnstake, setLoadingUnstake] = useState(false);
  const [loadingClaim, setLoadingClaim] = useState(false);
  const [isTransactionPending, setIsTransactionPending] = useState(false);
  const [isFetchingNFTs, setIsFetchingNFTs] = useState(false);

  // Refs for contracts and init
  const lpStakingContractRef = useRef(null);
  const lpNFTContractRef = useRef(null);
  const contractsInitializedRef = useRef(false);
  const initializeRetryCountRef = useRef(0);

  // Timers
  const lastFetchTimeRef = useRef(0);
  const fetchTimeoutRef = useRef(null);

  // We'll use this as a cap for how many NFTs to fetch
  const MAX_NFT_FETCH_COUNT = 10;

  /**
   * Checks for ephemeral RPC errors that should be silently retried or ignored.
   * We do NOT want to pop a user-facing toast on these.
   */
  const isCommonRpcError = useCallback((error) => {
    if (!error) return false;
    const eStr = JSON.stringify(error).toLowerCase();
    const msg = (error.message || '').toLowerCase();
    const dataMsg = (error?.data?.message || '').toLowerCase();

    // We add "missing revert data" and "call exception" checks here
    const ephemeralIndicators = [
      'block is out of range',
      'non-200 status code',
      'blockchain height exceeded',
      'missing revert data',
      'call exception'
    ];

    return ephemeralIndicators.some(
      (indicator) =>
        eStr.includes(indicator) ||
        msg.includes(indicator) ||
        dataMsg.includes(indicator)
    );
  }, []);

  /**
   * Retry helper with exponential backoff. It silently retries ephemeral errors, but
   * if it’s a real error or final attempt, it throws so you can show a toast (if needed).
   */
  const retryContractCall = useCallback(
    async (callFn, maxRetries = 3) => {
      let lastError;
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          return await callFn();
        } catch (error) {
          lastError = error;
          // If ephemeral, log or ignore it, then retry up to maxRetries
          if (!isCommonRpcError(error) || attempt === maxRetries - 1) {
            // If not ephemeral or final attempt, rethrow
            throw error;
          }
          // Otherwise wait, then retry
          await new Promise((resolve) => setTimeout(resolve, 1000 * 2 ** attempt));
        }
      }
      throw lastError;
    },
    [isCommonRpcError]
  );

  /**
   * Single user position fetch at index
   */
  const getUserPositionAtIndex = useCallback(
    async (userAddr, idx) => {
      try {
        const pos = await retryContractCall(() =>
          lpStakingContractRef.current.userPositions(userAddr, idx)
        );
        const pid = pos.positionId.toString();
        const tid = pos.tokenId.toString();
        if (pid === '0' && tid === '0') {
          return null;
        }
        return {
          positionId: pid,
          tokenId: tid,
          liquidity: pos.liquidity.toString(),
          stakingStartTimestamp: pos.stakingStartTimestamp?.toString() || '0',
          stakingEndTimestamp: pos.stakingEndTimestamp?.toString() || '0',
          stakingPeriod: pos.stakingPeriod?.toString() || '0',
        };
      } catch (err) {
        console.error(`Error fetching userPositions at index ${idx}:`, err);
        throw err;
      }
    },
    [retryContractCall]
  );

  /**
   * Fetch user’s staked positions
   */
  const fetchLPStakingPositions = useCallback(async () => {
    if (!account || !lpStakingContractRef.current || !contractsInitializedRef.current) return;
    try {
      const positions = [];
      for (let i = 0; i < 50; i++) {
        let p;
        try {
          p = await getUserPositionAtIndex(account, i);
        } catch {
          // If an ephemeral error or out of range call, break
          break;
        }
        if (p) positions.push(p);
      }
      setLpStakingPositions(positions);
    } catch (error) {
      if (!isCommonRpcError(error)) {
        // Real error
        console.error("Error fetching staked positions:", error);
        toast.error("Failed to fetch staked positions: " + error.message);
      }
      setLpStakingPositions([]);
    }
  }, [account, getUserPositionAtIndex, isCommonRpcError]);

  /**
   * Fetch total staked
   */
  const fetchContractLPData = useCallback(async () => {
    if (!lpStakingContractRef.current || !contractsInitializedRef.current) return;
    try {
      const totalRaw = await retryContractCall(() =>
        lpStakingContractRef.current.totalStakedLiquidity()
      );
      setTotalLPStaked(totalRaw.toString());
    } catch (error) {
      if (!isCommonRpcError(error)) {
        console.error("Error fetching total staked liquidity:", error);
        toast.error("Failed to fetch total staked liquidity: " + error.message);
      }
      setTotalLPStaked('0');
    }
  }, [retryContractCall, isCommonRpcError]);

  /**
   * Fetch user’s LP NFTs
   */
  const fetchUserLPNFTs = useCallback(async () => {
    if (!account || !provider || !contractsInitializedRef.current || !lpNFTsSupported || isFetchingNFTs) {
      if (!lpNFTsSupported) setUserLPNFTs([]);
      return;
    }
    const now = Date.now();
    if (now - lastFetchTimeRef.current < 10000) {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setTimeout(fetchUserLPNFTs, 10000);
      return;
    }
    lastFetchTimeRef.current = now;
    setIsFetchingNFTs(true);

    try {
      const signer = provider.getSigner();
      const nftC = new ethers.Contract(LPTOKEN_ADDRESS, UniswapV3PositionManagerABI, signer);
      lpNFTContractRef.current = nftC;

      let balRaw;
      try {
        // We do 1 retry for ephemeral error
        balRaw = await retryContractCall(() => nftC.balanceOf(account), 1);
      } catch (e) {
        // If ephemeral, skip or fallback
        if (isCommonRpcError(e)) {
          console.warn("Skipping NFT balance due to ephemeral error");
          setUserLPNFTs([]);
          setIsFetchingNFTs(false);
          return;
        } else {
          console.error("Error getting NFT balance:", e);
          setLpNFTsSupported(false);
          setUserLPNFTs([]);
          setIsFetchingNFTs(false);
          return;
        }
      }

      setLpNFTsSupported(true);

      if (balRaw.isZero()) {
        setUserLPNFTs([]);
        setIsFetchingNFTs(false);
        return;
      }
      const count = Math.min(balRaw.toNumber(), MAX_NFT_FETCH_COUNT);
      const items = [];
      for (let i = 0; i < count; i++) {
        try {
          const tokenId = await nftC.tokenOfOwnerByIndex(account, i);
          const pos = await nftC.positions(tokenId);
          const isCashLP =
            pos.token0.toLowerCase() === CASHISDEAD_ADDRESS.toLowerCase() ||
            pos.token1.toLowerCase() === CASHISDEAD_ADDRESS.toLowerCase();
          if (isCashLP) {
            items.push({
              tokenId: tokenId.toString(),
              liquidity: pos.liquidity.toString(),
              token0: pos.token0,
              token1: pos.token1
            });
          }
        } catch {
          // ignore single NFT read error
        }
      }
      setUserLPNFTs(items);
    } catch (error) {
      if (!isCommonRpcError(error)) {
        console.error("Error fetching LP NFTs:", error);
        toast.error("Failed to fetch LP NFTs: " + error.message);
      }
      setUserLPNFTs([]);
    } finally {
      setIsFetchingNFTs(false);
    }
  }, [
    account,
    provider,
    isFetchingNFTs,
    lpNFTsSupported,
    retryContractCall,
    isCommonRpcError
  ]);

  /**
   * Booster: fetch user’s boost % from BoosterNFT
   */
  const fetchUserBoostPercentage = useCallback(async () => {
    if (!account || !lpStakingContractRef.current || !isInitialized) return;
    try {
      const boosterAddr = await lpStakingContractRef.current.boosterNFT();
      const boosterContract = new ethers.Contract(
        boosterAddr,
        ["function getBoost(address) view returns (uint256)"],
        provider
      );
      const val = await boosterContract.getBoost(account);
      setUserBoostPercentage(val.toNumber());
    } catch (error) {
      if (!isCommonRpcError(error)) {
        console.error("Error fetching user boost:", error);
      }
      setUserBoostPercentage(0);
    }
  }, [account, provider, isInitialized, isCommonRpcError]);

  /**
   * Initialize the contract references
   */
  const initializeContracts = useCallback(async () => {
    if (!provider || !account || isInitializing || contractsInitializedRef.current) return;
    setIsInitializing(true);

    // We'll do up to 3 attempts for ephemeral issues:
    let attempts = 0;
    while (attempts < 3) {
      attempts++;
      try {
        const net = await provider.getNetwork();
        if (net.chainId !== NETWORK_CONFIG.chainId) {
          // We can't proceed if on the wrong chain
          setIsInitializing(false);
          return;
        }

        const signer = provider.getSigner();
        const code = await provider.getCode(LPCASHSTAKE_ADDRESS);
        if (code === '0x') {
          // Contract not deployed
          setIsInitializing(false);
          return;
        }
        lpStakingContractRef.current = new ethers.Contract(LPCASHSTAKE_ADDRESS, LPStakingABI, signer);

        // Check if LP NFT is valid
        let nftC = null;
        try {
          nftC = new ethers.Contract(LPTOKEN_ADDRESS, UniswapV3PositionManagerABI, signer);
          await nftC.callStatic.balanceOf(account);
          setLpNFTsSupported(true);
        } catch {
          nftC = null;
          setLpNFTsSupported(false);
        }
        lpNFTContractRef.current = nftC;

        contractsInitializedRef.current = true;
        setIsInitialized(true);

        // fetch initial data
        await Promise.all([
          fetchUserLPNFTs(),
          fetchLPStakingPositions(),
          fetchContractLPData()
        ]);

        break; // success
      } catch (error) {
        if (!isCommonRpcError(error)) {
          // It's a real error (e.g., "from address mismatch")
          console.error("Failed to init LP Staking:", error);
          lpStakingContractRef.current = null;
          lpNFTContractRef.current = null;
          contractsInitializedRef.current = false;
          setIsInitialized(false);
          break;
        } else {
          // ephemeral error: try again
          if (attempts === 3) {
            console.warn("Ephemeral error persists after 3 retries. Giving up init.");
            lpStakingContractRef.current = null;
            lpNFTContractRef.current = null;
            contractsInitializedRef.current = false;
            setIsInitialized(false);
          }
        }
      }
    }
    setIsInitializing(false);
  }, [
    provider,
    account,
    isInitializing,
    fetchUserLPNFTs,
    fetchLPStakingPositions,
    fetchContractLPData,
    isCommonRpcError
  ]);

  /**
   * Attempt init or reset
   */
  useEffect(() => {
    if (
      provider &&
      account &&
      chainId === NETWORK_CONFIG.chainId &&
      !isInitialized &&
      !isInitializing &&
      !contractsInitializedRef.current
    ) {
      initializeRetryCountRef.current = 0;
      initializeContracts();
    } else if ((!account || chainId !== NETWORK_CONFIG.chainId) && isInitialized) {
      setIsInitialized(false);
      contractsInitializedRef.current = false;
      lpStakingContractRef.current = null;
      lpNFTContractRef.current = null;
      setLpStakingPositions([]);
      setUserLPNFTs([]);
      setUserLPBalance('0');
      setUserLPAllowance('0');
      setTotalLPStaked('0');
      setIsInitializing(false);
      initializeRetryCountRef.current = 0;
    }
  }, [
    provider,
    account,
    chainId,
    isInitialized,
    isInitializing,
    initializeContracts
  ]);

  /**
   * If user changes wallet (account changes), re-initialize so we fetch the new addresses’ data.
   */
  useEffect(() => {
    if (!isChangingWallet) return;
    // Once the wallet finishing changing, re-init
    const handle = setTimeout(() => {
      if (provider && account && !isInitializing) {
        setIsInitialized(false);
        contractsInitializedRef.current = false;
      }
    }, 2000);
    return () => clearTimeout(handle);
  }, [isChangingWallet, provider, account, isInitializing]);

  /**
   * Periodic refresh
   */
  useEffect(() => {
    if (isInitialized && account) {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = setInterval(async () => {
        await fetchLPStakingPositions();
        await fetchContractLPData();
        if (lpNFTsSupported) {
          await fetchUserLPNFTs();
        }
      }, 60000);
    }
    return () => {
      if (fetchTimeoutRef.current) {
        clearInterval(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [
    isInitialized,
    account,
    fetchLPStakingPositions,
    fetchContractLPData,
    fetchUserLPNFTs,
    lpNFTsSupported
  ]);

  /**
   * Periodic booster check if staked
   */
  useEffect(() => {
    if (isInitialized && account) {
      fetchUserBoostPercentage();
      const handle = setInterval(fetchUserBoostPercentage, 30000);
      return () => clearInterval(handle);
    }
  }, [isInitialized, account, fetchUserBoostPercentage]);

  // ------------- User actions -------------

  /**
   * stakeLP
   */
  const stakeLP = useCallback(
    async (tokenId, stakingPeriod) => {
      if (!contractsInitializedRef.current || !lpStakingContractRef.current || !account) {
        return;
      }
      if (!lpNFTsSupported || !lpNFTContractRef.current) {
        toast.error("LP NFTs not supported");
        return;
      }
      setLoadingStake(true);
      try {
        const nftC = lpNFTContractRef.current;
        const appr = await nftC.getApproved(tokenId);
        if (appr.toLowerCase() !== LPCASHSTAKE_ADDRESS.toLowerCase()) {
          const txA = await nftC.approve(LPCASHSTAKE_ADDRESS, tokenId);
          setIsTransactionPending(true);
          await txA.wait();
          setIsTransactionPending(false);
        }
        const tx = await lpStakingContractRef.current.stake(tokenId, stakingPeriod);
        setIsTransactionPending(true);
        await tx.wait();
        setIsTransactionPending(false);

        setTimeout(() => {
          fetchLPStakingPositions();
          if (lpNFTsSupported) {
            fetchUserLPNFTs();
          }
          fetchContractLPData();
        }, 2000);
        toast.success("NFT staked!");
      } catch (error) {
        if (!isCommonRpcError(error)) {
          console.error("Error staking LP:", error);
          toast.error("Failed to stake: " + error.message);
        }
      } finally {
        setLoadingStake(false);
        setIsTransactionPending(false);
      }
    },
    [
      account,
      lpNFTsSupported,
      fetchLPStakingPositions,
      fetchUserLPNFTs,
      fetchContractLPData,
      isCommonRpcError
    ]
  );

  /**
   * unstakeLP
   */
  const unstakeLP = useCallback(
    async (positionId) => {
      if (!contractsInitializedRef.current || !lpStakingContractRef.current || !account) {
        return;
      }
      setLoadingUnstake(true);
      try {
        const tx = await lpStakingContractRef.current.unstake(positionId);
        setIsTransactionPending(true);
        await tx.wait();
        setIsTransactionPending(false);

        if (lpNFTsSupported) {
          fetchUserLPNFTs();
        }
        fetchLPStakingPositions();
        fetchContractLPData();

        toast.success("Position unstaked!");
      } catch (error) {
        if (!isCommonRpcError(error)) {
          console.error("Error unstaking LP:", error);
          toast.error("Failed to unstake: " + error.message);
        }
      } finally {
        setLoadingUnstake(false);
        setIsTransactionPending(false);
      }
    },
    [
      account,
      lpNFTsSupported,
      fetchUserLPNFTs,
      fetchLPStakingPositions,
      fetchContractLPData,
      isCommonRpcError
    ]
  );

  /**
   * claimRewards
   */
  const claimRewards = useCallback(
    async (positionId) => {
      if (!contractsInitializedRef.current || !lpStakingContractRef.current || !account) {
        return;
      }
      setLoadingClaim(true);
      try {
        let tx;
        if (typeof lpStakingContractRef.current.claimRewards === 'function') {
          tx = await lpStakingContractRef.current.claimRewards(positionId);
        } else {
          tx = await lpStakingContractRef.current.claim(positionId);
        }
        setIsTransactionPending(true);
        await tx.wait();
        setIsTransactionPending(false);

        fetchLPStakingPositions();
        toast.success("Rewards claimed!");
      } catch (error) {
        if (!isCommonRpcError(error)) {
          console.error("Error claiming rewards:", error);
          toast.error("Failed to claim: " + error.message);
        }
      } finally {
        setLoadingClaim(false);
        setIsTransactionPending(false);
      }
    },
    [account, fetchLPStakingPositions, isCommonRpcError]
  );

  /**
   * Force a data refresh from UI
   */
  const refreshData = useCallback(async () => {
    if (!contractsInitializedRef.current) return;
    await fetchLPStakingPositions();
    await fetchContractLPData();
    if (lpNFTsSupported) {
      await fetchUserLPNFTs();
    }
    await fetchUserBoostPercentage();
  }, [
    fetchLPStakingPositions,
    fetchContractLPData,
    fetchUserLPNFTs,
    fetchUserBoostPercentage,
    lpNFTsSupported
  ]);

  /**
   * Provide the final context to your dApp
   */
  const contextValue = {
    isInitialized,
    isInitializing,

    lpStakingPositions,
    userLPNFTs,
    totalLPStaked,
    userLPBalance,
    userLPAllowance,
    userBoostPercentage,
    lpNFTsSupported,

    loadingStake,
    loadingUnstake,
    loadingClaim,
    isTransactionPending,
    isFetchingNFTs,

    stakeLP,
    unstakeLP,
    claimRewards,
    refreshData,
  };

  return (
    <LPStakingContext.Provider value={contextValue}>
      {children}
    </LPStakingContext.Provider>
  );
};

export default LPStakingProvider;